import React, { FunctionComponent, useCallback, useState } from 'react';
import { Icon, Label, Loader, Modal, Popup } from 'semantic-ui-react';
import { apiClient } from 'axios-client';

interface DiarizationSegment {
  speaker: string;
  start: number;
  end: number;
  text: string;
}

interface TranscriptionData {
  transcription?: string;
  diarization?: DiarizationSegment[];
}

interface CallTranscriptionProps {
  id?: string;
  disabled?: boolean;
}

const speakerOrderColors = ['#f2711c', '#2185d0'];

const formatTimestamp = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

export const CallTranscription: FunctionComponent<CallTranscriptionProps> = ({
  id,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<TranscriptionData | null>(null);

  const fetchTranscription = useCallback(() => {
    if (data) {
      setOpen(true);
      return;
    }
    setError(false);
    setLoading(true);
    apiClient
      .get<TranscriptionData>(`/form/call/transcription/${id}`)
      .then((response) => {
        setData(response.data);
        setOpen(true);
      })
      .catch((err) => {
        console.error(err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [id, data]);

  if (loading) return <Loader size="tiny" active inline />;

  const isDisabled = disabled || error;

  return (
    <>
      <Icon
        name="comments"
        link={!isDisabled}
        disabled={isDisabled}
        title="Voir la transcription"
        onClick={isDisabled ? undefined : fetchTranscription}
      />
      <Modal open={open} onClose={() => setOpen(false)} closeIcon size="small">
        <Modal.Header>Conversation</Modal.Header>
        <Modal.Content scrolling>
          {data?.diarization?.length ? (
            <div>
              {(() => {
                const speakers = Array.from(
                  new Set(data.diarization.map((s) => s.speaker)),
                );
                return data.diarization.map((segment, i) => {
                  const colorIndex = speakers.indexOf(segment.speaker);
                  const color = speakerOrderColors[colorIndex] ?? '#767676';
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '6px',
                        marginBottom: '6px',
                      }}
                    >
                      <Popup
                        trigger={
                          <Label
                            size="mini"
                            style={{
                              backgroundColor: color,
                              color: '#fff',
                              cursor: 'default',
                              flexShrink: 0,
                            }}
                          >
                            {segment.speaker}
                          </Label>
                        }
                        content={`${formatTimestamp(
                          segment.start,
                        )} - ${formatTimestamp(segment.end)}`}
                        size="mini"
                        position="top center"
                        inverted
                      />
                      <span>{segment.text}</span>
                    </div>
                  );
                });
              })()}
            </div>
          ) : data?.transcription ? (
            <p style={{ whiteSpace: 'pre-wrap' }}>{data.transcription}</p>
          ) : (
            <p>Aucune transcription disponible.</p>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
};
